import { Component, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { getUnsubscribedRecipient$, resubscribeRecipient$ } from '@vendasta/protos/email/v1/admin';
import { SenderType as EmailSenderType } from '@vendasta/protos/email/v1/enums';
import type { GetUnsubscribedRecipientResponse } from '@vendasta/protos/email/v1/types';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable } from 'rxjs';

interface Recipient {
  email: string;
  senderId: string;
}

type SenderType = '' | 'partner' | 'business' | 'product';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';

@Component({
  selector: 'app-resubscribe',
  templateUrl: './resubscribe.component.html',
  styleUrls: ['./resubscribe.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    GalaxyAlertModule,
    GalaxyPopoverModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCardModule,
    MatIconModule,
    MatInputModule,
    GalaxyFormFieldModule,
  ],
})
export class ResubscribeComponent {
  senderType: SenderType = 'partner';
  isHelpPopoverOpen: boolean;
  searchResult$$: BehaviorSubject<Recipient> = new BehaviorSubject<Recipient>(null);
  searchForm: UntypedFormGroup;
  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  pos = PopoverPositions['TopRight'];

  constructor(
    formBuilder: UntypedFormBuilder,
    private alertService: SnackbarService,
  ) {
    this.searchForm = formBuilder.group({
      email: ['', Validators.required],
      senderId: [''],
      consent: [false, Validators.required],
    });
  }

  searchForUnsubscribed(): void {
    this.loading$$.next(true);
    this.searchForm.get('consent').setValue(false);
    const senderId = this.searchForm.get('senderId').value;
    const recipientEmail = this.searchForm.get('email').value;
    
    // Validate required fields based on sender type
    if ((this.senderType === 'partner' || this.senderType === 'business') && !senderId) {
      this.alertService.errorSnack('Please enter the required sender ID');
      this.loading$$.next(false);
      return;
    }
    let get$: Observable<GetUnsubscribedRecipientResponse>;
    switch (this.senderType) {
      case '':
        throw new Error('Cannot get unsubscribed for empty sender type');
      case 'business':
        get$ = getUnsubscribedRecipient$({
          senderType: EmailSenderType.SENDER_TYPE_BUSINESS,
          senderId,
          recipientEmail,
        });
        break;
      case 'partner':
        get$ = getUnsubscribedRecipient$({
          senderId,
          senderType: EmailSenderType.SENDER_TYPE_PARTNER,
          recipientEmail,
        });
        break;
      case 'product':
        get$ = getUnsubscribedRecipient$({ senderType: EmailSenderType.SENDER_TYPE_PARTNER, recipientEmail });
        break;
    }
    get$.subscribe(
      (response: GetUnsubscribedRecipientResponse) => {
        this.searchResult$$.next({ email: response.recipientEmail, senderId: senderId });
        this.loading$$.next(false);
      },
      (error) => {
        this.loading$$.next(false);
        if (error.status === 404) {
          this.alertService.errorSnack('Recipient not found');
        } else if (error.status === 400) {
          this.alertService.errorSnack('Search failed');
          console.error('search failed', error.error.message);
        } else {
          this.alertService.errorSnack('Search failed');
        }
      },
    );
  }

  resubscribeRecipient(recipient: Recipient): void {
    this.loading$$.next(true);

    let resub$: Observable<void>;
    switch (this.senderType) {
      case '':
        throw new Error('Cannot resubscribe for empty sender type');
      case 'business':
        resub$ = resubscribeRecipient$({
          senderType: EmailSenderType.SENDER_TYPE_BUSINESS,
          senderId: recipient.senderId,
          recipientEmail: recipient.email,
        });
        break;
      case 'partner':
        resub$ = resubscribeRecipient$({
          senderId: recipient.senderId,
          senderType: EmailSenderType.SENDER_TYPE_PARTNER,
          recipientEmail: recipient.email,
        });
        break;
      case 'product':
        resub$ = resubscribeRecipient$({
          senderType: EmailSenderType.SENDER_TYPE_PARTNER,
          recipientEmail: recipient.email,
        });
        break;
    }
    resub$.subscribe({
      next: () => {
        this.alertService.successSnack('Recipient resubscribed');
        this.searchResult$$.next(null);
        this.searchForm.get('email').setValue('');
        this.searchForm.get('senderId').setValue('');
        this.loading$$.next(false);
      },
      error: (error) => {
        this.loading$$.next(false);
        if (error.status === 404) {
          this.alertService.errorSnack('Failed to resubscribe, recipient not found');
        } else {
          this.alertService.errorSnack('Failed to resubscribe');
          console.error('failed to resubscribe', error?.error?.message);
        }
      },
    });
  }

  goBack(): void {
    this.searchResult$$.next(null);
    this.searchForm.get('email').setValue('');
    this.searchForm.get('senderId').setValue('');
  }

  openHelpNote(): void {
    this.isHelpPopoverOpen = true;
  }

  closeHelpNote(): void {
    this.isHelpPopoverOpen = false;
  }
}
